import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import { toast } from "react-hot-toast";
import CustomButton from "../../../../components/CustomButton";
import CustomText, { textStyles } from "../../../../components/CustomText";
import deletemodel from "../../../../assets/deletemodel.svg";

const DeleteTaskModal = ({ open, onClose, taskId, taskTitle, onTaskDeleted }) => {
  const { t } = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (!taskId) {
      toast.error(t("projectManagement.toast.noTaskSelected"));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/task-management/admin/tasks/${taskId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${decryptedToken}`,
        },
      });

      const data = await res.json();
      if (!data.error) {
        toast.success(t("projectManagement.toast.taskDeleted"));
        if (onTaskDeleted) onTaskDeleted(); // refresh tasks
        onClose();
      } else {
        toast.error(data.message || t("projectManagement.toast.taskDeleteFailed"));
      }
    } catch (err) {
      console.error("❌ Delete failed:", err);
      toast.error(t("projectManagement.toast.taskDeleteError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "5px",
          paddingRight: "5px",
        },
      }}
    >
      {/* Icon */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <img src={deletemodel} alt="delete" width={50} height={50} />
      </Box>

      {/* Title */}
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: "20px",
          fontWeight: 600,
          textAlign: "center",
          mb: 0,
          color: "#2C384C",
        }}
      >
        {t("deleteModal.title") || "Delete Task"}
      </CustomText>

      {/* Message */}
      <DialogContent sx={{ textAlign: "center", px: 2, pb: 1 }}>
        <CustomText
          sx={{
            ...textStyles.h1,
            fontSize: 18,
            fontWeight: 500,
            color: "#2C384C",
            textAlign: "center",
            lineHeight: "32px",
          }}
        >
          {t("deleteModal.confirmDeleteTask")}
        </CustomText>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          pb: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{
            fontSize: 15,
            textTransform: "none",
            borderRadius: "5px",
            fontFamily: "Poppins, sans-serif",
            color: "#006EC2",
            border: "2px solid #006EC2",
            px: 3,
            py: 0.8,
          }}
        >
          {t("deleteModal.cancel") || "Cancel"}
        </Button>

        <CustomButton
          onClick={handleDelete}
          disabled={loading}
          size="medium"
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {loading && (
                <CircularProgress
                  size={18}
                  thickness={4}
                  sx={{ color: "#ffffff" }}
                />
              )}
              {t("deleteModal.delete") || "Delete"}
            </Box>
          }
        />
      </DialogActions>
    </Dialog>
  );
};

export default DeleteTaskModal;
